import type { SwadeModConfigurationInterface } from '@/types/items';

/**
 * A comma-separated text list — a power's trappings, an item's notes — and the one fold every
 * `container.*` modification on such a field runs (owner's ruling 2026-09-24): `alter` overwrites the
 * whole text, `add` joins its value onto the current text separated by ", ", and `subtract` removes an
 * exact item, leaving no double commas behind. Shared by the `notes` detail strategy
 * (resolve-detail.ts) and the `trapping` policy field (container-fields.ts, applied in
 * rebuildContainerOverrides), so the two cannot drift apart.
 */

/** The items of a list: split on ',', each trimmed, blanks dropped. A finite number is one item. */
export function splitTextList(value: unknown): string[] {
    const text = typeof value === 'number' && Number.isFinite(value) ? String(value)
        : typeof value === 'string' ? value
        : '';
    return text.split(',').map((item) => item.trim()).filter((item) => item !== '');
}

type ListOperator = 'add' | 'subtract' | 'alter';
interface Contribution { operator: ListOperator, text: string }

/**
 * What a configuration contributes, or null. Only the three list operators count — `multiply`
 * means nothing to text, `block` means "prevent" everywhere else, and a null operator is a blank
 * form template — and a null, blank or non-primitive value contributes nothing, so a malformed
 * modification can never blank the field (the rule every strategy applies).
 */
const contributed = (configuration: SwadeModConfigurationInterface): Contribution | null => {
    const operator = (configuration?.operator ?? null) as string | null;
    if (operator !== 'add' && operator !== 'subtract' && operator !== 'alter') return null;
    const value: unknown = configuration?.value;
    if (typeof value === 'number' && Number.isFinite(value)) return { operator, text: String(value) };
    return typeof value === 'string' && value.trim() !== '' ? { operator, text: value } : null;
};

/**
 * Folds in fixed stages, so the result never depends on the order the configurations arrive in:
 * the LAST `alter` replaces the base outright (positional, as every other strategy's alter is),
 * then every `add` appends the items the list does not already hold, then every `subtract`
 * removes its items by exact match. An alter alone, or nothing at all, hands the text back
 * verbatim; list arithmetic re-joins the items with ", ", which is what normalises a stray
 * "Fire,,Ice" into "Fire, Ice". A null base is the empty string, never undefined — `undefined` is
 * reserved for "this field has no strategy".
 */
export function foldTextList(base: unknown, configurations: SwadeModConfigurationInterface[]): string {
    const entries: Contribution[] = [];
    for (const configuration of configurations ?? []) {
        const contribution = contributed(configuration);
        if (contribution !== null) entries.push(contribution);
    }

    let current = typeof base === 'string' ? base
        : typeof base === 'number' && Number.isFinite(base) ? String(base)
        : '';

    const alters = entries.filter((entry) => entry.operator === 'alter');
    if (alters.length > 0) current = alters[alters.length - 1].text;

    const adds = entries.filter((entry) => entry.operator === 'add');
    const subtracts = entries.filter((entry) => entry.operator === 'subtract');
    if (adds.length === 0 && subtracts.length === 0) return current;

    const items = splitTextList(current);
    for (const add of adds) {
        for (const item of splitTextList(add.text)) {
            if (!items.includes(item)) items.push(item);
        }
    }
    const removed = new Set(subtracts.flatMap((subtract) => splitTextList(subtract.text)));
    return items.filter((item) => !removed.has(item)).join(', ');
}
